import { CompositeId } from '@app/core/models/common.models';
import { arrayUniqueByProperty, assertArray } from '@app/core/utilities/array.utilities';
import {
  CriterionAdditionCriterionType,
  CriterionAdditionMatchType,
} from '@app/criterion-addition/shared/models/criterion-addition.models';
import {
  Destination,
  DestinationAdGroup,
  DestinationGroup,
  DestinationInstance,
  DestinationMatchType,
  DestinationOption,
  DestinationTarget,
  DestinationType,
  DestinationWarning,
  DestinationWarningType,
} from '@app/criterion-addition/shared/models/destination.models';

const destinationGroups: Record<DestinationTarget, DestinationType[]> = {
  [DestinationTarget.SourceAdgroup]: [DestinationType.SourceAdgroup],
  [DestinationTarget.SelectedAdgroup]: [DestinationType.SelectedAdgroup],
  [DestinationTarget.SingleCampaign]: [
    DestinationType.AmazonCreatePatAdgroup,
    DestinationType.AmazonCreateKeywordAdgroup,
    DestinationType.AmazonCreateExactKeywordAdgroup,
    DestinationType.AmazonCreatePhraseKeywordAdgroup,
    DestinationType.AmazonCreateBroadKeywordAdgroup,
    DestinationType.AmazonCreateBrandKeywordAdgroup,
    DestinationType.AmazonCreateGenericKeywordAdgroup,
    DestinationType.AmazonCreateCompetitorKeywordAdgroup,

    DestinationType.WalmartCreateKeywordAdgroup,
    DestinationType.WalmartCreateExactKeywordAdgroup,
    DestinationType.WalmartCreateBroadKeywordAdgroup,
    DestinationType.WalmartCreateBrandKeywordAdgroup,
    DestinationType.WalmartCreateGenericKeywordAdgroup,
    DestinationType.WalmartCreateCompetitorKeywordAdgroup,
  ],
  [DestinationTarget.MatchTypeCampaigns]: [
    DestinationType.AmazonCreateExactKeywordCampaignAndAdgroup,
    DestinationType.AmazonCreatePhraseKeywordCampaignAndAdgroup,
    DestinationType.AmazonCreateBroadKeywordCampaignAndAdgroup,

    DestinationType.WalmartCreateExactKeywordCampaignAndAdgroup,
    DestinationType.WalmartCreatePhraseKeywordCampaignAndAdgroup,
    DestinationType.WalmartCreateBroadKeywordCampaignAndAdgroup,
  ],
  [DestinationTarget.BrandCompetitorGenericCampaigns]: [
    DestinationType.AmazonCreateBrandKeywordCampaignAndAdgroup,
    DestinationType.AmazonCreateGenericKeywordCampaignAndAdgroup,
    DestinationType.AmazonCreateCompetitorKeywordCampaignAndAdgroup,

    DestinationType.WalmartCreateBrandKeywordCampaignAndAdgroup,
    DestinationType.WalmartCreateGenericKeywordCampaignAndAdgroup,
  ],
};

const destinationKeywordGroups: Record<CriterionAdditionMatchType, DestinationType[]> = {
  [CriterionAdditionMatchType.Exact]: [
    DestinationType.AmazonCreateExactKeywordAdgroup,
    DestinationType.AmazonCreateExactKeywordCampaignAndAdgroup,

    DestinationType.WalmartCreateExactKeywordAdgroup,
    DestinationType.WalmartCreateExactKeywordCampaignAndAdgroup,
  ],
  [CriterionAdditionMatchType.Phrase]: [
    DestinationType.AmazonCreatePhraseKeywordAdgroup,
    DestinationType.AmazonCreatePhraseKeywordCampaignAndAdgroup,

    DestinationType.WalmartCreatePhraseKeywordCampaignAndAdgroup,
  ],
  [CriterionAdditionMatchType.Broad]: [
    DestinationType.AmazonCreateBroadKeywordAdgroup,
    DestinationType.AmazonCreateBroadKeywordCampaignAndAdgroup,

    DestinationType.WalmartCreateBroadKeywordCampaignAndAdgroup,
  ],
};

const radioDestinations: DestinationType[] = [DestinationType.AmazonCreatePatAdgroup];

const radioCriterionTypes: CriterionAdditionCriterionType[] = [
  CriterionAdditionCriterionType.Pat,
  CriterionAdditionCriterionType.Placement,
  CriterionAdditionCriterionType.MobileApplication,
  CriterionAdditionCriterionType.YoutubeVideo,
  CriterionAdditionCriterionType.YoutubeChannel,
];

export function isValidDestination(destination: DestinationInstance, isRadioCriterion: boolean): boolean {
  if ((isRadioDestination(destination) || isRadioCriterion) && destination.negative != null) {
    return true;
  }

  return !!destination.matchType;
}

export function getDestinationAdgroupId(destination: DestinationInstance): CompositeId | undefined {
  if (!destination || destination.destinationType === DestinationType.SourceAdgroup) {
    return undefined;
  }

  return (
    destination.adspertAdgroupId || destination.selectedAdgroupId || destination.effectiveDestination?.adspertAdgroupId
  );
}

export function getDestinationInGroups(
  destinations: DestinationGroup[],
  destinationType: DestinationType,
): Destination | undefined {
  for (const group of destinations) {
    for (const dest of group.destinations) {
      if (dest.destinationType === destinationType) {
        return dest;
      }
    }
  }
}

export function getDestinationGroupFromDestinationType(
  destinations: DestinationGroup[],
  destinationType: DestinationType,
): DestinationGroup {
  for (const group of destinations) {
    for (const dest of group.destinations) {
      if (dest.destinationType === destinationType) {
        return group;
      }
    }
  }
}

export function isAdGroupDestination(
  destination: Destination | DestinationAdGroup | DestinationInstance,
): destination is DestinationAdGroup {
  return !!getDestinationAdgroupId(destination as DestinationInstance);
}

export function getDestinationId(destination: Destination | DestinationAdGroup | DestinationInstance): string {
  return getDestinationAdgroupId(destination as DestinationInstance) || (destination as Destination).destinationType;
}

export function sortDestinationOptions<T extends DestinationOption | DestinationInstance>(options: T[]): T[] {
  if (!options) {
    return;
  }

  return [...options].sort((a, b) => (a.negative ? 1 : -1) - (b.negative ? 1 : -1));
}

export function destinationsToGroup(destinations: Destination[]): DestinationGroup[] {
  return Object.entries(destinationGroups).reduce((groups, [target, group]) => {
    const groupDestinations = destinations.filter((destination) => group.includes(destination.destinationType));

    if (groupDestinations.some((destination) => destination.allowed)) {
      groups.push({
        target: target as DestinationTarget,
        destinations: groupDestinations.map((destination) => ({
          ...destination,
          destinationOptions: sortDestinationOptions(destination?.destinationOptions),
        })),
      });
    }

    return groups;
  }, [] as DestinationGroup[]);
}

export function isDestinationOptionSelected(
  option: DestinationOption,
  instances: DestinationInstance[],
  isRadioDestination: boolean,
): boolean {
  return !!getDestinationOptionInstance(option, instances, isRadioDestination);
}

export function isDestinationOptionDisabled(
  option: DestinationOption,
  instances: DestinationInstance[],
  destination: Destination | DestinationAdGroup,
  isRadioDestination: boolean,
  selected: boolean,
): boolean | DestinationWarningType {
  if (assertArray(option.warnings).filter((warning) => warning.warningType && warning.critical).length) {
    return !selected;
  }

  if (
    !isRadioDestination &&
    instances.some(({ matchType, negative }) => matchType === option.matchType && !negative !== !option.negative)
  ) {
    return DestinationWarningType.NegativeWithPositiveCriterionNotAllowed;
  }

  if (!isAdGroupDestination(destination) && !option.negative) {
    if (
      [CriterionAdditionMatchType.Exact, CriterionAdditionMatchType.Phrase, CriterionAdditionMatchType.Broad].includes(
        option.matchType,
      )
    ) {
      if (isKeywordDestination(destination, option.matchType)) {
        return DestinationWarningType.MatchTypeMandatoryForDestination;
      }
    }
  }

  return false;
}

export function isRadioDestination(destination: { destinationType: DestinationType } | DestinationType): boolean {
  const destinationType = typeof destination === 'string' ? destination : destination.destinationType;

  return radioDestinations.includes(destinationType);
}

export function isRadioCriterion(
  criterion: { criterionType: CriterionAdditionCriterionType } | CriterionAdditionCriterionType,
): boolean {
  const criterionType = typeof criterion === 'string' ? criterion : criterion.criterionType;

  return radioCriterionTypes.includes(criterionType);
}

export function getDestinationOptionInstance(
  option: DestinationOption,
  instances: DestinationInstance[],
  isRadioDestination: boolean,
): DestinationInstance | undefined {
  return assertArray(instances).find(
    (instance) =>
      ((isRadioDestination && !option.matchType) || instance.matchType === option.matchType) &&
      !instance.negative === !option.negative,
  );
}

export function isKeywordDestination(destination: Destination, matchType: CriterionAdditionMatchType): boolean {
  return destinationKeywordGroups[matchType]?.includes(destination.destinationType);
}

export function getDefaultMatchType(destination: DestinationType): CriterionAdditionMatchType | undefined {
  return Object.entries(destinationKeywordGroups).find(([, destinations]) =>
    destinations.includes(destination),
  )?.[0] as CriterionAdditionMatchType;
}

export function getDestinationSortId(destinations: DestinationGroup[], instance: DestinationInstance): string {
  const adGroupId = getDestinationAdgroupId(instance);

  const destinationGroup = getDestinationGroupFromDestinationType(
    destinations,
    adGroupId ? DestinationType.SelectedAdgroup : instance.destinationType,
  );
  let sortId = destinations.findIndex((group) => group.target === destinationGroup?.target).toString();

  if (!adGroupId) {
    sortId +=
      '-' +
      destinationGroup?.destinations.findIndex(({ destinationType }) => destinationType === instance.destinationType);
  } else {
    sortId += '-' + adGroupId;
  }

  return sortId;
}

export function setDestinationMatchTypeOptions(
  isRadioDestination: boolean,
  destinationMatchTypes: DestinationMatchType[],
): DestinationMatchType[] {
  return destinationMatchTypes
    .map((select) => {
      select = {
        ...select,
        instances: sortDestinationOptions(select.instances),
        destination: {
          ...select.destination,
          destinationOptions: sortDestinationOptions(
            assertArray(select.destination.destinationOptions).map((option) => {
              const selected = isDestinationOptionSelected(option, select.instances, isRadioDestination);
              const disabledOrWarning = isDestinationOptionDisabled(
                option,
                select.instances,
                select.destination,
                isRadioDestination,
                selected,
              );
              const disabled = !!disabledOrWarning;

              const warnings: DestinationWarning[] = <any>[
                ...assertArray(option.warnings),
                ...assertArray(typeof disabledOrWarning === 'string' ? { warningType: disabledOrWarning } : undefined),
              ];

              return {
                ...option,
                selected,
                disabled,
                warnings,
              } as DestinationOption;
            }),
          ),
        },
      };

      if (isRadioDestination) {
        select.radioValue = select.destination.destinationOptions.find((option) => option.selected)?.negative;
      }

      return select;
    })
    .sort((a, b) => (a.sortId > b.sortId ? 1 : -1));
}

export function parseToDestinationMatchTypes(
  selected: DestinationInstance[],
  destinations: DestinationGroup[],
  adGroups: DestinationAdGroup[],
  isRadioDestination: boolean,
): DestinationMatchType[] {
  return setDestinationMatchTypeOptions(
    isRadioDestination,
    selected.reduce((matchTypes, instance) => {
      const adGroupId = getDestinationAdgroupId(instance);
      const id = getDestinationId(instance);

      let destination: Destination | DestinationAdGroup;
      let destinationMatchType: DestinationMatchType = matchTypes.find((matchType) => matchType.id === id);

      const sortId = getDestinationSortId(destinations, instance);

      if (!destinationMatchType) {
        destination = adGroupId
          ? adGroups.find(({ adspertAdgroupId }) => adspertAdgroupId === adGroupId)
          : getDestinationInGroups(destinations, instance.destinationType);
      }

      if (!destinationMatchType && destination) {
        destinationMatchType = {
          id,
          sortId,
          destination,
          instances: [],
          warnings: instance?.warnings,
        };
        matchTypes.push(destinationMatchType);
      }

      if (destinationMatchType && isValidDestination(instance, isRadioDestination))
        destinationMatchType.instances.push(instance);

      return matchTypes.map((mt) => {
        if (!mt.destination['adspertAdgroupId'] && mt.instances?.[0]?.effectiveDestination)
          mt.destination = { ...mt.destination, ...mt.instances[0].effectiveDestination };

        mt.destinationType = mt.destination?.['destinationType'] || mt.instances?.[0]?.destinationType;
        mt.warnings = arrayUniqueByProperty(
          mt.instances.reduce((acc, cur) => [...acc, ...assertArray(cur.warnings)], []),
          'warningType',
        );

        return mt;
      });
    }, [] as DestinationMatchType[]),
  );
}

export function isCreateDestinationType(destination: DestinationInstance): boolean {
  return (
    !destination?.effectiveDestination?.adspertAdgroupId &&
    ![DestinationType.SelectedAdgroup, DestinationType.SourceAdgroup].includes(destination.destinationType)
  );
}
